import {
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import React from 'react'

import { isTeamExternalIntegrationAzure } from 'components/common/mapped-entities/azure/authentication/azure-integration-utils'
import { isTeamExternalIntegrationGcp } from 'components/common/mapped-entities/gcp/authentication/gcp-integration-utils'
import PageSection from 'components/common/PageSection'
import { StyledRouterLink } from 'components/common/StyledRouterLink'
import Icon from 'components/Icon'
import { TeamIntegrationTable } from 'components/status-checks/common/team-integration-table/TeamIntegrationTable'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useStatusChecksSearchContext } from 'components/status-checks/StatusChecksSearchState'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import useSearch from 'hooks/use-search'
import { useTranslation } from 'hooks/use-translation'
import { type TeamExternalIntegration } from 'types/api/status-checks/status-check'
import { naturalSortBy } from 'utils/list'
import { messageFromResponseError } from 'utils/strings'

/**
 * Only non-Azure and non-GCP integrations should be displayed.
 * Integrations must also have a key added, which means the
 * `lastAuthenticationStatus` field should exist.
 */
function isValidSavedIntegration(integration: TeamExternalIntegration) {
  return (
    integration.lastAuthenticationStatus &&
    !isTeamExternalIntegrationGcp(integration) &&
    !isTeamExternalIntegrationAzure(integration)
  )
}

// TODO add tests
export const AuthenticationList: React.FC = () => {
  const statusChecksUrls = getStatusChecksUrls()
  const { trans } = useTranslation()
  const {
    hasErrorExternalIntegrations,
    externalIntegrationsError,
    isFetchingExternalIntegrations,
    teamExternalIntegrations
  } = useStatusCheckExternalIntegrationsContext()

  const savedIntegrations = teamExternalIntegrations.filter(
    isValidSavedIntegration
  )

  const sortedIntegrations = naturalSortBy(savedIntegrations, 'name', 'asc')

  const searchState = useStatusChecksSearchContext()
  const { searchValue, setSearchValue } = searchState.authentications

  // search setup
  const searchMapFn = React.useCallback(
    (integration: TeamExternalIntegration) =>
      [integration.name].filter(Boolean),
    []
  )
  const searchIntegrations = useSearch(sortedIntegrations, searchMapFn)
  const filteredIntegrations = React.useMemo(
    () => searchIntegrations(searchValue),
    [searchValue, searchIntegrations]
  )

  const handleSearchInputChange = ({ target: { value } }) => {
    setSearchValue(String(value))
  }

  const handleOnChange = async () => {
    setSearchValue('')
  }

  return (
    <Stack
      data-test-status-check-authentication-list
      direction="column"
      spacing={2}
      sx={{ marginTop: 3 }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        marginBottom={2}
        spacing={5}
      >
        <Typography>
          {trans('status_checks.authentication_list.description', {
            externalIntegrationType: trans('status_checks.label.singular')
          })}
        </Typography>
        <Button
          data-test-create-status-check-authentication
          component={StyledRouterLink}
          size="small"
          startIcon={<Icon icon="plus" />}
          sx={{ height: 'fit-content', minWidth: 'fit-content' }}
          to={statusChecksUrls.newAuthentication()}
          variant="contained"
        >
          {trans('authentication')}
        </Button>
      </Stack>
      <PageSection
        errorMessage={messageFromResponseError(
          externalIntegrationsError,
          trans('status_checks.authentication_list.loading.error')
        )}
        hasError={hasErrorExternalIntegrations}
        isLoading={isFetchingExternalIntegrations}
      >
        <Stack direction="column">
          <Stack direction="row">
            <TextField
              data-test-status-check-auth-search
              disabled={
                isFetchingExternalIntegrations || savedIntegrations?.length < 1
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon="search" />
                  </InputAdornment>
                )
              }}
              label="Search"
              size="small"
              value={searchValue}
              onChange={handleSearchInputChange}
            />
          </Stack>
          <TeamIntegrationTable
            savedIntegrations={filteredIntegrations}
            onChange={handleOnChange}
          />
        </Stack>
      </PageSection>
    </Stack>
  )
}
